/**
 * Build modes that represent a real, locked-down deployment target — dev-only tooling must never be
 * reachable there, even via `?dev=true`.
 *
 * Everything else (`development`, `beta`, `liveDev`, `test`, ...) is a team-facing QA/test build where
 * `?dev=true` intentionally continues to unlock dev tooling — quick-login test users, email-capture
 * popups, form autofill, etc. are the point of those builds.
 *
 * This used to be an allowlist of the opposite kind ("production"/"staging" only), which is how
 * `motodev` and `sepdevpreview` — both real, externally-reachable hosts — ended up unprotected: nobody
 * updated the list when those modes were added. A later attempt fixed that by inverting to "anything
 * that isn't literally `development` mode," but that swung too far the other way and broke `?dev=true`
 * for `beta`/`liveDev`/`test`, which are legitimate QA builds that are supposed to keep it. There is no
 * mechanical signal (`DEV`, mode name, hostname) that alone distinguishes "a real deployment target
 * with an audience" from "a team QA build" — that's a judgment call, so it has to be an explicit list.
 * Keep it a list of *known real hosts/roles* (matching `.env.<mode>`'s actual deploy target) rather
 * than trying to derive it, and add to it deliberately when a new one is introduced.
 */
const LOCKED_DOWN_MODES = new Set(["production", "staging", "motodev", "sepdevpreview"]);

/**
 * True only for the modes in `LOCKED_DOWN_MODES`. Use this (or `isDevToolsEnabled`) instead of
 * re-checking `import.meta.env.MODE`/`DEV`/hostname ad hoc; several components used to each
 * reimplement this test slightly differently, and the drift between them is what let dev-only
 * functionality leak into deployed environments it shouldn't have.
 */
export function isProductionEnvironment(): boolean {
    return LOCKED_DOWN_MODES.has(import.meta.env.MODE);
}

/**
 * Gates dev-only UI (dev taskbar, form autofill, demo email popup, god mode, etc.)
 * behind `?dev=true` in every non-locked-down build, so deployed builds stay clean by default.
 */
export function isDevToolsEnabled(): boolean {
    if (isProductionEnvironment()) return false;
    return new URLSearchParams(window.location.search).get("dev") === "true";
}
